// src/components/lms/LMSPageGuard.jsx — FINAL
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLMSAccess } from '../../hooks/useLMSAccess';
import { useLMSAuth } from '../../hooks/useLMSAuth';

const STAFF_KEY = 'lms_staff_session';

export default function LMSPageGuard({ pageKey, children }) {
  const { account }                                   = useLMSAuth();
  const { staffSession, canAccess, canEdit, loading } = useLMSAccess();
  const navigate                                      = useNavigate();
  const toastRef                                      = useRef();
  const isAdmin                                       = !!account;

  const storedSession = (() => {
    try { return JSON.parse(localStorage.getItem(STAFF_KEY) || 'null'); }
    catch(_) { return null; }
  })();

  const activeSession = staffSession || storedSession;
  const hasSession    = isAdmin || !!activeSession?.staff_id;
  const sessionPages  = staffSession?.pages || storedSession?.pages || {};
  const hasAccess     = isAdmin || pageKey === 'dashboard' || sessionPages[pageKey]?.can_access === true;
  const editAllowed   = isAdmin || sessionPages[pageKey]?.can_edit === true;
  const viewOnly      = hasAccess && !editAllowed && pageKey !== 'dashboard';

  useEffect(() => {
    if (!isAdmin && loading && storedSession?.staff_id) return;
    if (!hasSession) {
      navigate(`/lms/staff-login?redirect=${encodeURIComponent(pageKey)}`, { replace: true });
      return;
    }
    if (!isAdmin && !loading && staffSession && !hasAccess) {
      navigate('/lms/dashboard', { replace: true });
    }
  }, [isAdmin, staffSession, loading, hasAccess, hasSession, pageKey]);

  const showToast = () => {
    if (!toastRef.current) return;
    toastRef.current.style.opacity = '1';
    toastRef.current.style.transform = 'translateX(-50%) translateY(0)';
    clearTimeout(toastRef.current._t);
    toastRef.current._t = setTimeout(() => {
      if (toastRef.current) {
        toastRef.current.style.opacity = '0';
        toastRef.current.style.transform = 'translateX(-50%) translateY(10px)';
      }
    }, 3000);
  };

  if (isAdmin) return <>{children}</>;
  if (!hasSession) return null;
  if (!loading && staffSession && !hasAccess) return null;

  // Full access — render normally
  if (!viewOnly) return <>{children}</>;

  // VIEW ONLY MODE
  // Strategy: wrap content in a div, put a transparent overlay on top
  // The overlay captures ALL clicks and shows toast
  // CSS disables pointer events on interactive elements as backup
  return (
    <div style={{ position: 'relative' }}>

      {/* CSS: disable all interactive elements inside page content */}
      <style>{`
        #lms-viewonly-content button,
        #lms-viewonly-content input,
        #lms-viewonly-content select,
        #lms-viewonly-content textarea,
        #lms-viewonly-content [role="button"],
        #lms-viewonly-content [role="combobox"],
        #lms-viewonly-content [role="checkbox"],
        #lms-viewonly-content [role="switch"] {
          pointer-events: none !important;
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }
        #lms-viewonly-content form {
          pointer-events: none !important;
        }
      `}</style>

      {/* Page content — rendered but interactive elements are CSS-disabled */}
      <div id="lms-viewonly-content">
        {children}
      </div>

      {/* Full overlay that catches any click that gets through CSS */}
      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 998,
          cursor: 'not-allowed',
          background: 'transparent',
          // Don't block sidebar — sidebar is a separate DOM tree outside this guard
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          showToast();
        }}
      />

      {/* View-only badge */}
      <div style={{
        position: 'fixed', top: 68, right: 20, zIndex: 1001,
        background: '#FFF7ED', border: '1.5px solid #FED7AA',
        borderRadius: 10, padding: '7px 14px',
        fontSize: 12, fontWeight: 700, color: '#92400E',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        pointerEvents: 'none',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        👁 View Only Mode
      </div>

      {/* Toast */}
      <div ref={toastRef} style={{
        position: 'fixed', bottom: 32,
        left: '50%', transform: 'translateX(-50%) translateY(10px)',
        zIndex: 1002, background: '#1E293B', color: '#fff',
        borderRadius: 14, padding: '14px 22px',
        fontSize: 14, fontWeight: 700,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        display: 'flex', alignItems: 'center', gap: 10,
        opacity: 0,
        transition: 'opacity 0.3s, transform 0.3s',
        pointerEvents: 'none', whiteSpace: 'nowrap',
      }}>
        🔒 View Only &nbsp;
        <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: 13 }}>
          Ask admin to grant edit access
        </span>
      </div>
    </div>
  );
}